import {useAuth0} from "@auth0/auth0-react";
import {BusinessProfile, Campaign, DeepResearchTask, DeepResearchWorkflowExecution, Document, Strategy} from "@src/model/business_profile";
import {Post} from "../model/business_profile";


export function useBusinessProfileAPI() {
  const { getAccessTokenSilently } = useAuth0();

  const getBusinessProfile = (id: string): Promise<BusinessProfile> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        fetch(`${process.env.REACT_APP_API_URL}/business-profile/${id}`, {
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        })
          .then((response) => {
            if (response.status === 200) {
              response.json().then((data: BusinessProfile) => {
                var socialAccountStr = data.social_accounts?.map((account) => account.link).join(', ');
                //@ts-ignore
                data.social_accounts = socialAccountStr;
                resolve(data)
              }).catch(reject);
            } else {
              reject(new Error("Failed to fetch business profile: " + response.statusText));
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  const createBusinessProfile = (payload: BusinessProfile): Promise<BusinessProfile> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        fetch(`${process.env.REACT_APP_API_URL}/business-profile/admin`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        })
          .then((response) => {
            if (response.status === 200) {
              response.json().then((data: BusinessProfile) => resolve(data)).catch(reject);
            } else {
              reject(new Error("Failed to create business profile: " + response.statusText));
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  const updateBusinessProfile = (id: string, payload: Partial<BusinessProfile>): Promise<BusinessProfile> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        fetch(`${process.env.REACT_APP_API_URL}/business-profile/admin/${id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        })
          .then((response) => {
            if (response.status === 200) {
              response.json().then((data: BusinessProfile) => resolve(data)).catch(reject);
            } else {
              reject(new Error("Failed to update business profile: " + response.statusText));
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  const getAllBusinessProfiles = (): Promise<BusinessProfile[]> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        fetch(`${process.env.REACT_APP_API_URL}/business-profile/admin/list`, {
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        })
          .then((response) => {
            if (response.status === 200) {
              response.json().then((data: BusinessProfile[]) => resolve(data)).catch(reject);
            } else {
              reject(new Error("Failed to fetch business profiles: " + response.statusText));
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  const updateBusinessProfileCampaign = (id: string, payload: Partial<Campaign>): Promise<Campaign> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        fetch(`${process.env.REACT_APP_API_URL}/business-profile/admin/campaign/${id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        })
          .then((response) => {
            if (response.status === 200) {
              response.json().then((data: Campaign) => resolve(data)).catch(reject);
            } else {
              reject(new Error("Failed to update campaign: " + response.statusText));
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  const updateStrategy = (id: string, payload: Partial<Strategy>): Promise<Strategy> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        fetch(`${process.env.REACT_APP_API_URL}/business-profile/admin/strategy/${id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        })
          .then((response) => {
            if (response.status === 200) {
              response.json().then((data: Strategy) => resolve(data)).catch(reject);
            } else {
              reject(new Error("Failed to update strategy: " + response.statusText));
            }
          })

          .catch(reject);
      }).catch(reject);
    });
  };


  const deleteBusinessProfile = (id: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        fetch(`${process.env.REACT_APP_API_URL}/business-profile/admin/${id}`, {
          method: "DELETE",
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        })
          .then((response) => {
            if (response.status === 200) {
              resolve();
              reject(new Error("Failed to delete business profile: " + response.statusText));
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  const startTraining = (id: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        fetch(`${process.env.REACT_APP_API_URL}/business-profile/admin/start-training/${id}`, {
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        })
          .then((response) => {
            if (response.status === 200) {
              resolve();
            } else {
              reject(new Error("Failed to start training: " + response.statusText));
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  /**
   * Trigger Deep Research (Business Intelligence) for a business profile.
   * Admin-only endpoint; model should be "GEMINI" or "OPENAI".
   */
  const runBusinessIntelligenceDeepResearch = (businessProfileId: string, model: "GEMINI" | "OPENAI"): Promise<void> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        fetch(`${process.env.REACT_APP_API_URL}/deep-research/admin/generate-business-intelligence/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
              business_profile_id: businessProfileId,
            models: [model],
          }),
        })
          .then((response) => {
            if (response.ok) {
              resolve();
            } else {
              response.text().then(text => {
                reject(new Error(`Failed to start deep research: ${response.statusText} - ${text}`));
              }).catch(() => {
                reject(new Error(`Failed to start deep research: ${response.statusText}`));
              });
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  const addDocument = (id: string, file: File, usage: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("usage", usage);

        fetch(
          `${process.env.REACT_APP_API_URL}/business-profile/admin/${id}/add-document`,
          {
            method: "POST",
            headers: {
              authorization: `Bearer ${accessToken}`,
            },
            body: formData,
          },
        )
          .then((response) => {
            if (response.ok) {
              resolve();
            } else {
              response.text().then(text => {
                reject(new Error(`Failed to add document: ${response.statusText} - ${text}`));
              }).catch(() => {
                reject(new Error(`Failed to add document: ${response.statusText}`));
              });
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  const getAllDocuments = (): Promise<Document[]> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        fetch(`${process.env.REACT_APP_API_URL}/documents/admin/list`, {
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        })
          .then((response) => {
            if (response.status === 200) {
              response.json().then((data: Document[]) => resolve(data)).catch(reject);
            } else {
              reject(new Error("Failed to fetch documents: " + response.statusText));
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  const generateCampaign = (id: string): Promise<Campaign> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        fetch(
          `${process.env.REACT_APP_API_URL}/business-profile/admin/${id}/generate-campaign`,
          {
            method: "POST",
            headers: {
              authorization: `Bearer ${accessToken}`,
            },
          },
        )
          .then((response) => {
            if (response.ok) {
              response.json().then((data: Campaign) => resolve(data)).catch(reject);
            } else {
              response.text().then(text => {
                reject(new Error(`Failed to add document: ${response.statusText} - ${text}`));
              }).catch(() => {
                reject(new Error(`Failed to add document: ${response.statusText}`));
              });
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  const deleteCampaign = (campaignId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        fetch(`${process.env.REACT_APP_API_URL}/business-profile/admin/delete-campaign/${campaignId}`, {
          method: "DELETE",
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        })
          .then((response) => {
            if (response.ok) {
              resolve();
            } else {
              reject(new Error("Failed to delete campaign: " + response.statusText));
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  const generateImagesForStrategy = (businessProfileId: string, strategyId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        fetch(`${process.env.REACT_APP_API_URL}/business-profile/admin/${businessProfileId}/${strategyId}/generate-posts`, {
          method: "POST",
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        })
          .then((response) => {
            if (response.ok) {
              resolve();
            } else {
              reject(new Error("Failed to generate images for strategy: " + response.statusText));
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  const updatePost = (postId: string, payload: any): Promise<any> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        fetch(`${process.env.REACT_APP_API_URL}/business-profile/admin/post/${postId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        })
          .then((response) => {
            if (response.ok) {
              response.json().then((data) => resolve(data)).catch(reject);
            } else {
              reject(new Error("Failed to update post: " + response.statusText));
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  const removeDocument = (businessProfileId: string, documentId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        fetch(`${process.env.REACT_APP_API_URL}/business-profile/admin/${businessProfileId}/document/${documentId}`, {
          method: "DELETE",
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        })
          .then((response) => {
            if (response.ok) {
              resolve();
            } else {
              reject(new Error("Failed to remove document association: " + response.statusText));
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  const updatePostGenerativeOutput = (postId: string, generativeOutputId: string | null): Promise<any> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        fetch(`${process.env.REACT_APP_API_URL}/business-profile/admin/post/${postId}/generative-output`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${accessToken}`,
          },
          body: generativeOutputId ? JSON.stringify(generativeOutputId) : null,
        })
          .then((response) => {
            if (response.ok) {
              response.json().then((data) => resolve(data)).catch(reject);
            } else {
              reject(new Error("Failed to update post generative output: " + response.statusText));
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  const generateStrategyFromBI = (businessProfileId: string): Promise<Campaign> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        fetch(
          `${process.env.REACT_APP_API_URL}/business-profile/admin/${businessProfileId}/generate-strategy-from-bi`,
          {
            method: "POST",
            headers: {
              authorization: `Bearer ${accessToken}`,
            },
          },
        )
          .then((response) => {
            if (response.ok) {
              response.json().then((data: Campaign) => resolve(data)).catch(reject);
            } else {
              response.text().then(text => {
                reject(new Error(`Failed to generate strategy: ${response.statusText} - ${text}`));
              }).catch(() => {
                reject(new Error(`Failed to generate strategy: ${response.statusText}`));
              });
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  const generateCampaignFromDocument = (businessProfileId: string, documentId: string): Promise<Campaign> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        fetch(
          `${process.env.REACT_APP_API_URL}/business-profile/admin/${businessProfileId}/generate-campaign-from-document/${documentId}`,
          {
            method: "POST",
            headers: {
              authorization: `Bearer ${accessToken}`,
            },
          },
        )
          .then((response) => {
            if (response.ok) {
              response.json().then((data: Campaign) => resolve(data)).catch(reject);
            } else {
              response.text().then(text => {
                reject(new Error(`Failed to generate campaign: ${response.statusText} - ${text}`));
              }).catch(() => {
                reject(new Error(`Failed to generate campaign: ${response.statusText}`));
              });
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  const deleteImageFromPost = (postId: string, generativeOutputId: string | null, fileId: string | null = null): Promise<Post> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        const url = `${process.env.REACT_APP_API_URL}/business-profile/post/${postId}/generative-output`;
        const body: any = {};
        if (generativeOutputId) {
          body.generative_output_id = generativeOutputId;
        }
        if (fileId) {
          body.file_id = fileId;
        }

        fetch(
          url,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(body),
          },
        )
          .then((response) => {
            if (response.ok) {
              response.json().then((data: Post) => resolve(data)).catch(reject);
            } else {
              response.text().then(text => {
                reject(new Error(`Failed to remove image: ${response.statusText} - ${text}`));
              }).catch(() => {
                reject(new Error(`Failed to removeImage: ${response.statusText}`));
              });
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

  const deleteAllImagesFromPost = (postId: string): Promise<Post> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        const url = `${process.env.REACT_APP_API_URL}/business-profile/post/${postId}/generative-output/all`;

        fetch(
          url,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              authorization: `Bearer ${accessToken}`,
            },
          }
        )
          .then((response) => {
            if (response.ok) {
              response.json().then((data: Post) => resolve(data)).catch(reject);
            } else {
              reject(new Error("Failed to delete all images from post: " + response.statusText));
            }
          })
      }).catch(reject);
    });
  };

  const backfillScripts = (businessProfileId: string, campaignId?: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      getAccessTokenSilently().then((accessToken) => {
        let url = `${process.env.REACT_APP_API_URL}/business-profile/admin/${businessProfileId}/backfill-scripts`;
        if (campaignId) {
          url += `?campaignId=${campaignId}`;
        }

        fetch(url, {
          method: "POST",
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        })
          .then((response) => {
            if (response.ok) {
              response.text().then(resolve).catch(reject);
            } else {
              response.text().then(text => {
                reject(new Error(`Failed to start script backfill: ${response.statusText} - ${text}`));
              }).catch(() => {
                reject(new Error(`Failed to start script backfill: ${response.statusText}`));
              });
            }
          })
          .catch(reject);
      }).catch(reject);
    });
  };

    /**
     * Get research status (workflow executions) for a business profile
     */
    const getResearchStatus = (businessProfileId: string): Promise<DeepResearchWorkflowExecution[]> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/deep-research/status/${businessProfileId}`, {
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.ok) {
                            response.json().then((data: DeepResearchWorkflowExecution[]) => resolve(data)).catch(reject);
                        } else {
                            response.text().then(text => {
                                reject(new Error(`Failed to get research status: ${response.statusText} - ${text}`));
                            }).catch(() => {
                                reject(new Error(`Failed to get research status: ${response.statusText}`));
                            });
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    };

    /**
     * Debug helper: fetch raw Gemini interaction payload.
     * Useful for diagnosing provider-side failures.
     */
    const debugGeminiInteraction = (interactionId: string): Promise<any> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/deep-research/debug/gemini/${encodeURIComponent(interactionId)}`, {
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.ok) {
                            response.json().then(resolve).catch(reject);
                        } else {
                            response.text().then(text => {
                                reject(new Error(`Failed to fetch Gemini debug: ${response.statusText} - ${text}`));
                            }).catch(() => {
                                reject(new Error(`Failed to fetch Gemini debug: ${response.statusText}`));
                            });
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    };

    /**
     * Fix the JSON content/sections of an existing document using the backend cleanup service.
     */
    const fixDocumentJson = (documentId: string): Promise<Document> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/deep-research/admin/fix-document-json/${documentId}`, {
                    method: "POST",
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.ok) {
                            response.json().then((data: Document) => resolve(data)).catch(reject);
                        } else {
                            response.text().then(text => {
                                reject(new Error(`Failed to fix document JSON: ${response.statusText} - ${text}`));
                            }).catch(() => {
                                reject(new Error(`Failed to fix document JSON: ${response.statusText}`));
                            });
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    };

  return {
    getBusinessProfile,
    createBusinessProfile,
    updateBusinessProfile,
    getAllBusinessProfiles,
    deleteBusinessProfile,
    startTraining,
    runBusinessIntelligenceDeepResearch,
    addDocument,
    updateBusinessProfileCampaign,
    updateStrategy,
    updatePost,
    getAllDocuments,
    generateCampaign,
    deleteCampaign,
    generateImagesForStrategy,
    removeDocument,
    updatePostGenerativeOutput,
    generateCampaignFromDocument,
    generateStrategyFromBI,
    deleteImageFromPost,
    deleteAllImagesFromPost,
      backfillScripts,
      getResearchStatus,
      debugGeminiInteraction,
      fixDocumentJson
  };
}
